import { computed, defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useSweetAlert } from '@/composables/sweetalert';
import { RegionAction, RegionGetter } from '@/store/region/region.module';
import { Region } from '@/store/region/region.model';
import { RegionSchema } from '@/store/region/region.validator';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'RegionFormComponent',
  components: {
    BaseModalComponent,
  },
  props: {
    regionId: {
      type: Number,
    }
  },
  setup: (props) => {
    const store = useStore();
    const swal = useSweetAlert();
    const isEditMode = computed(() => props.regionId != null);
    const region = computed<Region | undefined>(() => store.getters[RegionGetter.BY_ID](props.regionId));
    const title = computed(() => isEditMode.value ? 'แก้ไขเขตตรวจราชการ' : 'เพิ่มเขตตรวจราชการ');
    const regionForm = ref<InstanceType<typeof BaseModalComponent>>();
    const presetColors = [
      '#999999', '#aabfd0', '#cac7fc', '#60c6f7',
      '#b8dffc', '#57ffff', '#bff3dc', '#5cf990',
      '#20e921', '#a1a1ff', '#fefcd5', '#fafc8e',
      '#ffff16', '#ffe365', '#f7c00b', '#a05c11',
      '#c87b23', '#dc9d50', '#eabb87'
    ];

    // form
    interface Form {
      id?: number | null;
      name: string;
      shortName: string;
      color: string;
    }
    const formData = reactive<Form>({
      id: null,
      name: '',
      shortName: '',
      color: '',
    });
    const validator = computed(() => RegionSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      name: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'name')),
      shortName: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'shortName')),
      color: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'color')),
    });
    const isValid = computed(() => !errors.name && !errors.color&& !errors.shortName);
    const isLoading = computed<boolean>(() => store.getters[RegionGetter.STATUS] === 'init' || store.getters[RegionGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        if (isEditMode.value) {
          formData.id = props.regionId;
          formData.name = region.value?.name ?? '';
          formData.shortName = region.value?.shortName ?? '';
          formData.color = region.value?.color ?? '#ffffff';
        }
        resolve();
      })
      .then(() => regionForm.value?.show());
    };

    // hide modal
    const hide = () => {
      regionForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.id = null;
        formData.name = '';
        formData.shortName = '';
        formData.color = '';
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(RegionAction.UPDATE, formData)
            .then(() => hide());
        } else {
          store.dispatch(RegionAction.CREATE, formData)
            .then(() => hide());
        }
      }
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(RegionAction.DELETE, formData)
              .then(() => hide());
          }
        });
    };

    return {
      title,
      formData,
      isEditMode,
      errors,
      submited,
      isLoading,
      regionForm,
      presetColors,
      show,
      hide,
      submitClick,
      deleteClick,
      unsubmit,
      clearForm
    };
  },
});
